import React from "react";

import { resyncActiveIncomingCalls } from "~/services/activeCallsService";
import { resolveSocketUrl } from "~/services/calling/config";
import { AppSocket } from "~/services/calling/socketClient";
import type { AgentPresence } from "~/services/calling/types";
import {
  applyAlarmSignal,
  applyCallSnapshot,
  applyCallTaken,
  applyIncomingCall,
  applyObserverJoined,
  applyObserverLeft,
  applySelfInvitedSuperviseSession,
  removeAnsweredCall,
  removeIncomingCallByUniqueId,
} from "~/context/call/callState";
import type { CallState } from "~/context/call/callState";
import {
  agentsAreEqual,
  normalizeAgent,
  normalizeAlarmSignal,
  normalizeIncomingCall,
  normalizeSocketIdentityValue,
  normalizeRealtimeEventName,
  buildEventMessage,
  parseAgentIdentity,
  isAlarmIncomingPayload,
  isIncomingTerminalCallState,
} from "~/context/call/helpers";
import type { SipLifecycleIdentity } from "~/context/call/useSipLifecycle";
import type { ConnectionHealthRecorder } from "~/context/call/connectionHealth/types";
import {
  INVITED_SUPERVISE_AUTO_ACCEPT_WINDOW_MS,
  preparePendingSupervise,
} from "~/context/call/superviseAutoAccept";
import type { CallStatePayload, PendingSupervise } from "~/services/calling/types";
import { inferAlarmPayloadFromAnyEvent } from "~/context/call/alarmPayloadInference";

export type UseCallSocketParams = {
  socketRef: React.MutableRefObject<AppSocket | null>;
  token: string | null;
  sipIdentityRef: React.MutableRefObject<SipLifecycleIdentity>;
  /** Stamped with `Date.now()` whenever a realtime agent roster arrives. */
  realtimeAgentsUpdatedAtRef: React.MutableRefObject<number>;
  updateState: (next: Partial<CallState>) => void;
  setState: React.Dispatch<React.SetStateAction<CallState>>;
  userExtensionId?: string | null;
  userSipExtension: string | null;
  userId?: string;
  userEmail?: string | null;
  /** When socket emits call:state with a terminal state, append missed-call history. */
  onIncomingTerminalState?: (
    payload: Pick<CallStatePayload, "callUuid" | "callId" | "id" | "state">
  ) => void;
  onQueueResyncRequested?: () => void;
  onQueueClaimed?: (payload: import("~/services/calling/types").QueueClaimedPayload) => void;
  onQueueClaimReleased?: (
    payload: import("~/services/calling/types").QueueClaimReleasedPayload
  ) => void;
  pendingSuperviseRef: React.MutableRefObject<PendingSupervise | null>;
  pendingSuperviseTimeoutRef: React.MutableRefObject<number | null>;
  trySuperviseAutoAcceptRef: React.MutableRefObject<() => boolean>;
  connectionHealthRecorderRef?: React.MutableRefObject<ConnectionHealthRecorder>;
};

export type UseCallSocketResult = {
  initializeSocket: () => void;
};

function normalizeCallStateName(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase().replace(/[\s-]+/g, "_");
  return normalized.length > 0 ? normalized : null;
}

function isHoldLockoutState(value: unknown): boolean {
  const normalized = normalizeCallStateName(value);
  if (!normalized) return false;
  return normalized === "held" || normalized === "hold" || normalized === "on_hold";
}

function shouldClearHoldLockoutMessage(value: unknown): boolean {
  const normalized = normalizeCallStateName(value);
  if (!normalized) return false;
  return (
    normalized === "active" ||
    normalized === "ended" ||
    normalized === "idle" ||
    normalized === "ringing" ||
    normalized === "dialing" ||
    normalized === "resume" ||
    normalized === "resumed"
  );
}

export function useCallSocket({
  socketRef,
  token,
  sipIdentityRef,
  realtimeAgentsUpdatedAtRef,
  updateState,
  setState,
  userExtensionId,
  userSipExtension,
  userId,
  userEmail,
  onIncomingTerminalState,
  onQueueResyncRequested,
  onQueueClaimed,
  onQueueClaimReleased,
  pendingSuperviseRef,
  pendingSuperviseTimeoutRef,
  trySuperviseAutoAcceptRef,
  connectionHealthRecorderRef,
}: UseCallSocketParams): UseCallSocketResult {
  const snapshotReceivedRef = React.useRef(false);
  const resyncFallbackTimerRef = React.useRef<number | null>(null);
  const socketHandlersRef = React.useRef({
    onIncomingTerminalState,
    onQueueResyncRequested,
    onQueueClaimed,
    onQueueClaimReleased,
  });

  React.useEffect(() => {
    socketHandlersRef.current = {
      onIncomingTerminalState,
      onQueueResyncRequested,
      onQueueClaimed,
      onQueueClaimReleased,
    };
  });

  const clearResyncFallbackTimer = React.useCallback(() => {
    if (resyncFallbackTimerRef.current == null) return;
    window.clearTimeout(resyncFallbackTimerRef.current);
    resyncFallbackTimerRef.current = null;
  }, []);

  const scheduleResyncFallback = React.useCallback(() => {
    clearResyncFallbackTimer();
    resyncFallbackTimerRef.current = window.setTimeout(() => {
      resyncFallbackTimerRef.current = null;
      if (snapshotReceivedRef.current) return;
      void resyncActiveIncomingCalls(setState).catch((error) => {
        console.warn("[CallSocket] Active call resync fallback failed", error);
      });
    }, 3000);
  }, [clearResyncFallbackTimer, setState]);

  React.useEffect(() => () => clearResyncFallbackTimer(), [clearResyncFallbackTimer]);

  const initializeSocket = React.useCallback(() => {
    const socketUrl = resolveSocketUrl();
    if (!socketUrl) {
      updateState({
        lastError: "Missing Socket.IO URL.",
        socketStatus: "error",
      });
      return;
    }

    if (!socketRef.current) {
      socketRef.current = new AppSocket();
    }

    const extensionFromUser =
      normalizeSocketIdentityValue(userSipExtension) ??
      normalizeSocketIdentityValue(userExtensionId);
    const resolvedExtension =
      extensionFromUser ?? sipIdentityRef.current.extension ?? undefined;
    const resolvedSipUsername =
      sipIdentityRef.current.sipUsername ?? resolvedExtension ?? undefined;

    if (!resolvedExtension) {
      return;
    }

    const existingSocket = socketRef.current;
    if (!existingSocket?.isConnected()) {
      updateState({ socketStatus: "connecting" });
    }
    snapshotReceivedRef.current = false;
    scheduleResyncFallback();

    socketRef.current.connect(
      socketUrl,
      token,
      {
        onConnect: () => {
          connectionHealthRecorderRef?.current.recordSocketConnect();
          updateState({ socketStatus: "connected" });
          socketHandlersRef.current.onQueueResyncRequested?.();
        },
        onQueueResyncRequested: () => {
          socketHandlersRef.current.onQueueResyncRequested?.();
        },
        onQueueClaimed: (payload) => {
          socketHandlersRef.current.onQueueClaimed?.(payload);
        },
        onQueueClaimReleased: (payload) => {
          socketHandlersRef.current.onQueueClaimReleased?.(payload);
        },
        onCallSnapshot: (payload) => {
          snapshotReceivedRef.current = true;
          clearResyncFallbackTimer();
          setState((prev) => applyCallSnapshot(prev, payload));
          socketHandlersRef.current.onQueueResyncRequested?.();
        },
        onCallTaken: (payload) => {
          setState((prev) => applyCallTaken(prev, payload, resolvedExtension));
        },
        onCallEnded: (payload) => {
          console.log("[IncomingCall][SocketEnded]", {
            uniqueId: payload.uniqueId,
            extension: payload.extension,
            endedAt: payload.endedAt,
          });
          setState((prev) => {
            const next = removeIncomingCallByUniqueId(prev, payload.uniqueId);
            return removeAnsweredCall(next, payload.uniqueId);
          });
        },
        onDisconnect: (reason) => {
          connectionHealthRecorderRef?.current.recordSocketDisconnect();
          updateState({
            socketStatus: "disconnected",
            lastSocketDisconnectReason: reason,
            backendHoldLockoutActive: false,
            holdLockoutMessage: null,
          });
        },
        onReconnectAttempt: () => {
          connectionHealthRecorderRef?.current.recordSocketReconnectAttempt();
          updateState({ socketStatus: "connecting" });
        },
        onReconnect: () => {
          connectionHealthRecorderRef?.current.recordSocketConnect();
          updateState({ socketStatus: "connected" });
          socketHandlersRef.current.onQueueResyncRequested?.();
        },
        onConnectError: (error) => {
          updateState({
            socketStatus: "error",
            lastError: error.message,
          });
        },
        onIncomingCall: (payload, meta) => {
          console.log("[IncomingCall][Socket]", {
            eventName: meta?.eventName ?? null,
            source: meta?.source ?? null,
            id: payload.id ?? null,
            callUuid: payload.callUuid ?? null,
            callId: payload.callId ?? null,
            callerNumber: payload.callerNumber ?? null,
            customerName: payload.customerName ?? null,
            service: payload.service ?? null,
            signalType: payload.signalType ?? null,
            description: payload.description ?? null,
            incomingMessage: payload.description ?? payload.metadata ?? null,
            receivedAtMs: payload.receivedAtMs ?? null,
            metadata: payload.metadata ?? null,
            rawPayload: payload,
          });
          setState((prev) => {
            if (isAlarmIncomingPayload(payload)) {
              return applyAlarmSignal(prev, normalizeAlarmSignal(payload));
            }
            return applyIncomingCall(prev, normalizeIncomingCall(payload));
          });
        },
        onAgentList: (payloads) => {
          realtimeAgentsUpdatedAtRef.current = Date.now();
          const nextAgents = payloads.map(normalizeAgent);
          setState((prev) =>
            agentsAreEqual(prev.agents, nextAgents)
              ? prev
              : { ...prev, agents: nextAgents }
          );
        },
        onAgentStatus: (payload) =>
          setState((prev) => {
            realtimeAgentsUpdatedAtRef.current = Date.now();
            const next = normalizeAgent(payload);
            const existing = prev.agents.filter((agent) => agent.id !== next.id);
            return {
              ...prev,
              agents: [...existing, next],
            };
          }),
        onInviteConference: (payload) =>
          updateState({
            pendingInvite: payload,
          }),
        onObserverJoined: (payload) => {
          const isSelfInvited =
            resolvedExtension != null &&
            payload.observer.extension === resolvedExtension;

          setState((prev) => {
            const withObserver = applyObserverJoined(prev, payload);
            if (!isSelfInvited || !resolvedExtension) return withObserver;

            return applySelfInvitedSuperviseSession(
              withObserver,
              payload,
              resolvedExtension
            );
          });

          if (!isSelfInvited) return;

          preparePendingSupervise(
            pendingSuperviseRef,
            pendingSuperviseTimeoutRef,
            payload.observer.mode,
            INVITED_SUPERVISE_AUTO_ACCEPT_WINDOW_MS
          );
          trySuperviseAutoAcceptRef.current();
        },
        onObserverLeft: (payload) => {
          setState((prev) => applyObserverLeft(prev, payload));
        },
        onAnyEvent: (eventName, args) => {
          if (eventName === "connect" || eventName === "disconnect") {
            return;
          }

          const normalizedName = normalizeRealtimeEventName(eventName);
          const payload = args.length <= 1 ? args[0] : args;
          const isCallStateEvent =
            normalizedName === "call:state" ||
            (normalizedName.includes("call") && normalizedName.includes("state"));
          const callStatePayload =
            isCallStateEvent &&
            payload &&
            typeof payload === "object" &&
            !Array.isArray(payload)
              ? (payload as CallStatePayload)
              : null;

          if (
            normalizedName.includes("alarm") ||
            normalizedName.includes("signal") ||
            normalizedName.includes("scaip")
          ) {
            const inferredAlarmPayload = inferAlarmPayloadFromAnyEvent(
              payload,
              normalizedName
            );
            if (
              inferredAlarmPayload &&
              isAlarmIncomingPayload(inferredAlarmPayload)
            ) {
              setState((prev) =>
                applyAlarmSignal(prev, normalizeAlarmSignal(inferredAlarmPayload))
              );
            }
          }

          if (isCallStateEvent) {
            if (
              callStatePayload &&
              "state" in callStatePayload &&
              isIncomingTerminalCallState(
                typeof callStatePayload.state === "string"
                  ? callStatePayload.state
                  : undefined
              )
            ) {
              socketHandlersRef.current.onIncomingTerminalState?.({
                callUuid: callStatePayload.callUuid,
                callId: callStatePayload.callId,
                id: callStatePayload.id,
                state: callStatePayload.state,
              });
            }
          }

          if (callStatePayload) {
            if (isHoldLockoutState(callStatePayload.state)) {
              const message = buildEventMessage(callStatePayload, "").trim();
              setState((prev) => {
                const lockoutChanged = !prev.backendHoldLockoutActive;
                const messageChanged = message
                  ? prev.holdLockoutMessage !== message
                  : false;
                if (!lockoutChanged && !messageChanged) return prev;
                return {
                  ...prev,
                  backendHoldLockoutActive: true,
                  holdLockoutMessage: messageChanged
                    ? message
                    : prev.holdLockoutMessage,
                };
              });
            } else if (shouldClearHoldLockoutMessage(callStatePayload.state)) {
              setState((prev) =>
                !prev.backendHoldLockoutActive && prev.holdLockoutMessage == null
                  ? prev
                  : {
                      ...prev,
                      backendHoldLockoutActive: false,
                      holdLockoutMessage: null,
                    }
              );
            }
          }

          if (normalizedName.includes("security")) {
            setState((prev) => ({
              ...prev,
              securityEvents: [
                {
                  id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
                  eventName,
                  message: buildEventMessage(payload, "Security event received."),
                  atMs: Date.now(),
                },
                ...prev.securityEvents,
              ].slice(0, 30),
            }));
          }

          if (normalizedName.includes("agent") && normalizedName.includes("join")) {
            const agent = parseAgentIdentity(payload);
            if (agent.id || agent.name) {
              setState((prev) => {
                const agentId = agent.id ?? `joined-${Date.now()}`;
                const joinedAgent: AgentPresence = {
                  id: agentId,
                  name: agent.name ?? agentId,
                  status: "online",
                  profileImageUrl: null,
                };
                const existing = prev.agents.filter((item) => item.id !== agentId);
                const participants =
                  prev.conferenceRoom &&
                  !prev.participants.some((item) => item.id === agentId)
                    ? [...prev.participants, joinedAgent]
                    : prev.participants;
                return {
                  ...prev,
                  agents: [...existing, joinedAgent],
                  participants,
                  agentJoinedEvents: [
                    {
                      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
                      eventName,
                      agentId: agent.id,
                      agentName: agent.name,
                      atMs: Date.now(),
                    },
                    ...prev.agentJoinedEvents,
                  ].slice(0, 30),
                };
              });
            }
          }
        },
      },
      {
        userId: userId ?? undefined,
        agentId: userId ?? undefined,
        email: userEmail ?? undefined,
        extension: resolvedExtension,
        sipUsername: resolvedSipUsername,
      }
    );
  }, [
    clearResyncFallbackTimer,
    realtimeAgentsUpdatedAtRef,
    scheduleResyncFallback,
    setState,
    sipIdentityRef,
    socketRef,
    token,
    updateState,
    userEmail,
    userExtensionId,
    userId,
    userSipExtension,
    pendingSuperviseRef,
    pendingSuperviseTimeoutRef,
    trySuperviseAutoAcceptRef,
    connectionHealthRecorderRef,
  ]);

  return { initializeSocket };
}
